import { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Stack, MenuItem, Alert } from '@mui/material';
import type { Technician, CreateTechnicianInput } from '../../api/technicians.api';

interface Props {
  open: boolean;
  initial?: Technician | null;
  onClose: () => void;
  onSubmit: (input: CreateTechnicianInput) => Promise<void>;
  error?: string | null;
}

const empty: CreateTechnicianInput = { technicianName: '', status: 'active' };

export function TechnicianFormDialog({ open, initial, onClose, onSubmit, error }: Props) {
  const isEdit = Boolean(initial);
  const [form, setForm] = useState<CreateTechnicianInput>(empty);

  useEffect(() => {
    setForm(
      initial
        ? {
            technicianName: initial.technicianName,
            mobile: initial.mobile ?? undefined,
            aadhaar: initial.aadhaar ?? undefined,
            panNumber: initial.panNumber ?? undefined,
            stateId: initial.stateId,
            skillCategory: initial.skillCategory ?? undefined,
            experienceYears: initial.experienceYears,
            status: initial.status,
          }
        : empty,
    );
  }, [initial, open]);

  const set = <K extends keyof CreateTechnicianInput>(k: K, v: CreateTechnicianInput[K]) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isEdit ? `Edit Technician — ${initial?.technicianCode}` : 'Create Technician'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField label="Technician Name" value={form.technicianName} onChange={(e) => set('technicianName', e.target.value)} required fullWidth />
          <Stack direction="row" spacing={2}>
            <TextField label="Mobile" value={form.mobile ?? ''} onChange={(e) => set('mobile', e.target.value)} fullWidth />
            <TextField label="Aadhaar" value={form.aadhaar ?? ''} onChange={(e) => set('aadhaar', e.target.value)} fullWidth />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField label="PAN" value={form.panNumber ?? ''} onChange={(e) => set('panNumber', e.target.value.toUpperCase())} fullWidth />
            <TextField label="State ID" type="number" value={form.stateId ?? ''} onChange={(e) => set('stateId', e.target.value ? Number(e.target.value) : null)} fullWidth />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField label="Skill Category" value={form.skillCategory ?? ''} onChange={(e) => set('skillCategory', e.target.value)} fullWidth />
            <TextField label="Experience (yrs)" type="number" value={form.experienceYears ?? ''} onChange={(e) => set('experienceYears', e.target.value ? Number(e.target.value) : null)} fullWidth />
          </Stack>
          <TextField select label="Status" value={form.status} onChange={(e) => set('status', e.target.value as CreateTechnicianInput['status'])} fullWidth>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onSubmit(form)}>{isEdit ? 'Save' : 'Create'}</Button>
      </DialogActions>
    </Dialog>
  );
}
